import { ApplicationRef, Injectable, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { MenuController } from '@ionic/angular';
import type { RutaDiaItemLista } from './ruta-dia-storage.service';

export type AccionHojaRuta = 'caracterizacion' | 'plan' | 'registrar' | 'detalles';

export type ContextoAccionHojaRuta = {
  tipo: AccionHojaRuta;
  row?: RutaDiaItemLista | null;
};

/** Id del ion-menu usado como panel overlay de acciones de Hoja de Ruta (side="end"). */
export const RUTA_ACCION_PANEL_MENU_ID = 'ruta-accion-panel';

@Injectable({ providedIn: 'root' })
export class RutaAccionPanelService {
  private readonly menuController = inject(MenuController);
  private readonly appRef = inject(ApplicationRef);
  private readonly _ctx = new BehaviorSubject<ContextoAccionHojaRuta | null>(null);
  private readonly _guardado = new Subject<void>();

  /** Tras guardar «Registrar visita» u otro dato offline desde el panel de hoja de ruta. */
  readonly guardado$ = this._guardado.asObservable();

  get menuId(): string {
    return RUTA_ACCION_PANEL_MENU_ID;
  }

  getAbierto(): boolean {
    return this._ctx.value !== null;
  }

  getContexto(): ContextoAccionHojaRuta | null {
    return this._ctx.value;
  }

  /**
   * Abre el panel overlay. Tras cerrar un popover, Ionic/Angular a veces aún no ha aplicado
   * el cambio de `[disabled]` del ion-menu; `tick()` + un frame extra evita que `open()` falle
   * y quede “pendiente” hasta el próximo click (que dispara otra detección de cambios).
   */
  abrir(tipo: AccionHojaRuta, row?: RutaDiaItemLista | null): void {
    this._ctx.next({ tipo, row: row ?? null });
    this.appRef.tick();

    const intentarAbrir = (): void => {
      void this.menuController.enable(true, RUTA_ACCION_PANEL_MENU_ID);
      void this.menuController.open(RUTA_ACCION_PANEL_MENU_ID).then(ok => {
        if (!ok) {
          requestAnimationFrame(() => {
            void this.menuController.open(RUTA_ACCION_PANEL_MENU_ID);
          });
        }
      });
    };

    requestAnimationFrame(() => {
      intentarAbrir();
    });
  }

  cerrar(): void {
    this.menuController.close(RUTA_ACCION_PANEL_MENU_ID);
  }

  alCerrarMenu(): void {
    this._ctx.next(null);
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cerrar();
    }
  }

  onGuardado(): void {
    this._guardado.next();
  }
}

